import {
  IEmailCommunication,
  PolicyType,
  SendgridMessageEventType,
} from "shared";

import { CustomIframe } from "../../../../IFrame";
import { IPillProps, Pill } from "../../../../../../common/Atoms/Pill";

interface EmailCommunicationProps {
  email: IEmailCommunication;
  messageDomId?: string;
  isHighlighted?: boolean;
}

export function EmailCommunication({
  email,
  messageDomId,
  isHighlighted = false,
}: EmailCommunicationProps) {
  const formatSourcePolicyLabel = (policy: IEmailCommunication[`policies`][number]) => {
    if (policy.type === PolicyType.ON_DEMAND_PLACEHOLDER) {
      return `On Demand Email`;
    }

    return policy.title;
  };

  const formatSourcePolicyTypeLabel = (policy: IEmailCommunication[`policies`][number]) => {
    if (policy.type === PolicyType.ON_DEMAND_PLACEHOLDER) {
      return `On Demand Email`;
    }

    return policy.type;
  };

  const statusColorMap: Record<string, IPillProps[`color`]> = {
    [SendgridMessageEventType.Delivered]: `green`,
    [SendgridMessageEventType.Processed]: `gray`,
    [SendgridMessageEventType.Open]: `blue`,
    [SendgridMessageEventType.Click]: `indigo`,
    [SendgridMessageEventType.Deferred]: `amber`,
    [SendgridMessageEventType.Dropped]: `red`,
    [SendgridMessageEventType.Bounce]: `red`,
    [SendgridMessageEventType.SpamReport]: `red`,
  };

  const statusLabelMap: Record<string, string> = {
    [SendgridMessageEventType.SpamReport]: `Spam report`,
    [SendgridMessageEventType.Open]: `Opened`,
  };

  const getStatusPill = (status?: string) => {
    if (!status) return null;
    const label = statusLabelMap[status] || (status.charAt(0).toUpperCase() + status.slice(1));

    return (
      <Pill
        size={ `xs` }
        bordered
        color={ statusColorMap[status] || `gray` }
        tooltip={ `Latest Status` }
        message={ label }
      />
    );
  };

  const createdAt = email.createdAt ? new Date(email.createdAt) : null;
  const subject = email.emailContent?.subject;
  const html = email.emailContent?.htmlContent || ``;
  const attachmentsListCsv = email.emailContent?.attachments || [];
  const alsoSentTo = email.alsoSentTo || [];

  return (
    <div
      id={ messageDomId }
      className={ `flex justify-end` }>
      <div className={ `max-w-3xl` }>
        <div className={ `flex items-center justify-end gap-2 mb-1` }>
          { getStatusPill(email.latestStatus as string | undefined) }
          { Array.isArray(email.policies) && email.policies.length > 0 ? (
            <>
              <Pill
                size={ `xs` }
                bordered
                color={ `indigo` }
                tooltip={ `Created from ${formatSourcePolicyTypeLabel(email.policies[0])}` }
                message={ email.policies.map(policy => formatSourcePolicyLabel(policy)).join(`, `) }
              />

            </>
          ) : null }
          <div className={ `text-xs text-gray-500` }>
            { createdAt ? createdAt.toLocaleString() : `` }
          </div>
        </div>
        <div className={ `rounded-2xl rounded-tr-md bg-indigo-50 border border-indigo-200 shadow-xs overflow-hidden ${isHighlighted ? `ring-2 ring-amber-300` : ``} transition-all duration-300` }>
          <div className={ `px-4 pt-3` }>
            <div className={ `text-xs text-gray-500` }>
              { `From: ` }<span className={ `font-medium text-gray-700` }>{ email.from }</span>
              { ` · To: ` }<span className={ `font-medium text-gray-700` }>{ email.to }</span>
              { email.replyTo ? (
                <>
                  { ` · Reply-To: ` }<span className={ `text-gray-700` }>{ email.replyTo }</span>
                </>
              ) : null }
            </div>
            { alsoSentTo.length > 0 ? (
              <div className={ `mt-1 text-xs text-gray-500 italic` }>
                { `Also sent to: ${alsoSentTo.join(`, `)}` }
              </div>
            ) : null }
            { subject ? (
              <div className={ `mt-1 text-sm font-semibold text-gray-900` }>
                { subject }
              </div>
            ) : null }
            { attachmentsListCsv.length > 0 ? (
              <div className={ `mt-2 text-xs text-gray-600` }>
                <span className={ `font-medium text-gray-700` }>{ `Attachments:` }</span>{ ` ` }
                { attachmentsListCsv.join(`, `) }
              </div>
            ) : null }
          </div>
          <div className={ `mt-2` }>
            { html ? (
              <CustomIframe
                style={ {
                  width: `100%`,
                  minHeight: `270px`,
                  border: `none`,
                  backgroundColor: `white`,
                } }
                srcDoc={ html }
              />
            ) : (
              <div className={ `p-4 text-sm text-gray-500 italic` }>
                { `Archive not available` }
              </div>
            ) }
          </div>
        </div>
      </div>
    </div>
  );
}
